import { useCallback } from 'react';
import { css } from 'glamor';
import { useDispatch } from 'react-redux';
import { Link, useLocation, matchPath } from 'react-router-dom-v5';
import { CreditCard, LogOut, User } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import { signOut } from '../../processes/sessionProcesses';

const itemBaseClass = css({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  gap: 10,
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: 13,
  lineHeight: '1.5em',
  fontWeight: 500,
  color: '#475569',
  borderRadius: 6,
  cursor: 'pointer',
  transition: 'background-color 150ms',
  textDecoration: 'none',
  border: 'none',
  background: 'none',
});

const itemLinkIdleClass = css({
  ':hover': { backgroundColor: '#E2E8F0' },
});

const itemLinkActiveClass = css({
  backgroundColor: '#E0F2FE',
});

const itemLogoutClass = css({
  color: '#ef4444',
  ':hover': { backgroundColor: '#FEF2F2' },
});

const headerWrapperStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid #f1f5f9',
};

const displayNameStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#000000',
  margin: 0,
  lineHeight: '18px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const emailStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#64748b',
  margin: 0,
  lineHeight: '16px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const menuItemsWrapperStyle: React.CSSProperties = { padding: '4px 6px' };

const logoutWrapperStyle: React.CSSProperties = {
  borderTop: '1px solid #f1f5f9',
  padding: '4px 6px',
};

const defaultIconStyle: React.CSSProperties = { stroke: '#64748b' };
const logoutIconStyle: React.CSSProperties = { stroke: '#ef4444' };

function MenuLink({
  to,
  onClick,
  children,
}: {
  to: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const { pathname } = useLocation();
  const isActive = !!matchPath(pathname, { path: to });

  return (
    <Link
      to={to}
      onClick={onClick}
      className={css(itemBaseClass, isActive ? itemLinkActiveClass : itemLinkIdleClass).toString()}
    >
      {children}
    </Link>
  );
}

type UserMenuPopupProps = {
  displayName: string;
  email?: string;
  onClose: () => void;
  className?: string;
};

export function UserMenuPopup({ displayName, email, onClose, className }: UserMenuPopupProps) {
  const dispatch = useDispatch();

  const handleSignOut = useCallback(() => {
    signOut(dispatch);
    onClose();
  }, [dispatch, onClose]);

  return (
    <div
      className={cn(
        'z-50 bg-white rounded-[10px] shadow-lg border border-[#e2e8f0] overflow-hidden animate-in fade-in-0 zoom-in-95',
        className,
      )}
    >
      {/* User header */}
      <div style={headerWrapperStyle}>
        <p style={displayNameStyle}>{displayName}</p>
        {email && <p style={emailStyle}>{email}</p>}
      </div>

      {/* Menu items */}
      <div style={menuItemsWrapperStyle}>
        <MenuLink to="/profile/edit/personal" onClick={onClose}>
          <User size={15} className="shrink-0" style={defaultIconStyle} />
          Minha conta
        </MenuLink>
        <MenuLink to="/finance/subscriptions" onClick={onClose}>
          <CreditCard size={15} className="shrink-0" style={defaultIconStyle} />
          Financeiro
        </MenuLink>
      </div>

      {/* Logout */}
      <div style={logoutWrapperStyle}>
        <button
          type="button"
          onClick={handleSignOut}
          className={css(itemBaseClass, itemLogoutClass).toString()}
        >
          <LogOut size={15} className="shrink-0" style={logoutIconStyle} />
          Sair
        </button>
      </div>
    </div>
  );
}
